/**
 * ============================================================================
 * API СЕРВИС (Улучшенная версия)
 * ============================================================================
 * Обновлено: 01.02.2026 - исправлен API URL для HTTPS
 * ============================================================================
 */

import axios from 'axios'
import accountSocket from './accountSocket'

// ============================================================================
// КОНФИГУРАЦИЯ
// ============================================================================

const CONFIG = {
	BASE_URL: process.env.VUE_APP_API_URL || '/api/v1',
	TIMEOUT: {
		DEFAULT: 10000,
		UPLOAD: 10 * 60 * 1000,
	},
	STORAGE_KEYS: {
		ACCESS_TOKEN: 'access_token',
		REFRESH_TOKEN: 'refresh_token',
	},
}

// ============================================================================
// УТИЛИТЫ
// ============================================================================

function getStoredToken(key) {
	const token = localStorage.getItem(key)
	return token && token !== 'undefined' && token !== 'null' ? token : null
}

function setStoredTokens(accessToken, refreshToken) {
	localStorage.setItem(CONFIG.STORAGE_KEYS.ACCESS_TOKEN, accessToken)
	localStorage.setItem(CONFIG.STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
	accountSocket.updateToken(accessToken)
	scheduleTokenRefresh()
}

function clearStoredTokens() {
	localStorage.removeItem(CONFIG.STORAGE_KEYS.ACCESS_TOKEN)
	localStorage.removeItem(CONFIG.STORAGE_KEYS.REFRESH_TOKEN)
	if (tokenRefreshTimer) {
		clearTimeout(tokenRefreshTimer)
		tokenRefreshTimer = null
	}
	accountSocket.disconnect()
}

// ============================================================================
// OFFLINE QUEUE (очередь запросов при потере соединения)
// ============================================================================

let offlineQueue = []
let isOnline = navigator.onLine

window.addEventListener('online', () => {
	console.log('🌐 Соединение восстановлено, обрабатываем очередь...')
	isOnline = true
	processOfflineQueue()
	
	// Уведомляем индикатор сохранения
	window.dispatchEvent(new CustomEvent('connection:online'))
})

window.addEventListener('offline', () => {
	console.log('📡 Соединение потеряно, запросы будут добавлены в очередь')
	isOnline = false
	
	// Уведомляем индикатор сохранения
	window.dispatchEvent(new CustomEvent('connection:offline'))
})

function addToOfflineQueue(request) {
	offlineQueue.push({ ...request, _fromOfflineQueue: true })
	console.log(`📦 Запрос добавлен в offline очередь (${offlineQueue.length} в очереди)`)
}

async function processOfflineQueue() {
	if (offlineQueue.length === 0) return

	console.log(`🔄 Обработка ${offlineQueue.length} запросов из очереди...`)

	const queue = [...offlineQueue]
	offlineQueue = []

	for (let index = 0; index < queue.length; index += 1) {
		const request = queue[index]
		try {
			await apiClient(request)
		} catch (error) {
			if (!isOnline || !error?.response) {
				offlineQueue = [request, ...queue.slice(index + 1), ...offlineQueue]
				console.warn('📡 Соединение снова недоступно. Возвращаем запросы в очередь.')
				return
			}
			console.error('❌ Ошибка выполнения запроса из offline очереди:', error)
		}
	}
}

// ============================================================================
// ОБРАБОТКА REFRESH TOKEN (с защитой от race condition)
// ============================================================================

let isRefreshing = false
let refreshSubscribers = []
let tokenRefreshTimer = null
const ACCESS_TOKEN_TTL_MS = 8 * 60 * 60 * 1000
const TOKEN_REFRESH_LEAD_MS = 30 * 60 * 1000
const TOKEN_REFRESH_RETRY_MS = 60 * 1000

function subscribeTokenRefresh(callback) {
	refreshSubscribers.push(callback)
}

function onTokenRefreshed(token) {
	refreshSubscribers.forEach(callback => callback(token))
	refreshSubscribers = []
}

function onTokenRefreshFailed(error) {
	refreshSubscribers.forEach(callback => callback(null, error))
	refreshSubscribers = []
}

async function refreshAccessToken() {
	const refreshToken = getStoredToken(CONFIG.STORAGE_KEYS.REFRESH_TOKEN)

	if (!refreshToken) {
		throw new Error('No refresh token available')
	}

	const response = await axios.post(`${CONFIG.BASE_URL}/auth/refresh`, {
		refreshToken,
	})

	const { accessToken, refreshToken: newRefreshToken } =
		response.data.data.tokens
	setStoredTokens(accessToken, newRefreshToken)

	// Уведомляем об обновлении токена
	window.dispatchEvent(new CustomEvent('auth:token-refreshed'))

	return accessToken
}

// Проактивное обновление токена
function scheduleTokenRefresh(delayMs = Math.max(60 * 1000, ACCESS_TOKEN_TTL_MS - TOKEN_REFRESH_LEAD_MS)) {
	if (tokenRefreshTimer) {
		clearTimeout(tokenRefreshTimer)
	}

	tokenRefreshTimer = setTimeout(async () => {
		try {
			console.log('🔄 Проактивное обновление токена...')
			await refreshAccessToken()
			console.log('✅ Токен обновлён проактивно')
		} catch (error) {
			console.error('❌ Ошибка проактивного обновления токена:', error)
			if (getStoredToken(CONFIG.STORAGE_KEYS.REFRESH_TOKEN)) {
				scheduleTokenRefresh(TOKEN_REFRESH_RETRY_MS)
			}
		}
	}, delayMs)
}

// Запустить таймер при загрузке модуля
if (getStoredToken(CONFIG.STORAGE_KEYS.ACCESS_TOKEN)) {
	scheduleTokenRefresh()
}

// ============================================================================
// СОЗДАНИЕ КЛИЕНТА
// ============================================================================

const apiClient = axios.create({
	baseURL: CONFIG.BASE_URL,
	timeout: CONFIG.TIMEOUT.DEFAULT,
	headers: {
		'Content-Type': 'application/json',
	},
})

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================

apiClient.interceptors.request.use(
	async config => {
		// Проверка online для критичных операций (POST, PUT, DELETE)
		if (!isOnline && ['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase())) {
			if (!config.url?.includes('/auth/')) {
				const offlineError = new Error('OFFLINE_QUEUE_PENDING')
				offlineError.code = 'OFFLINE_QUEUE_PENDING'
				offlineError.isQueuedOffline = true
				offlineError.wasInQueue = Boolean(config._fromOfflineQueue)
				offlineError.config = config
				return Promise.reject(offlineError)
			}
			return Promise.reject(new Error('Нет подключения к интернету'))
		}

		// Публичные роуты - не добавляем токен
		const publicRoutes = ['/auth/login', '/auth/register', '/auth/refresh']
		const isPublicRoute = publicRoutes.some(route => config.url?.includes(route))

		// Добавление токена только для защищённых роутов
		const token = getStoredToken(CONFIG.STORAGE_KEYS.ACCESS_TOKEN)
		if (token && !config.skipAuth && !isPublicRoute) {
			// Проверяем не истёк ли токен
			try {
				const payload = JSON.parse(atob(token.split('.')[1]))
				const expiresAt = payload.exp * 1000
				const now = Date.now()
				const timeUntilExpiry = expiresAt - now
				
				// Если токен истёк или истечёт в течение 5 минут - обновляем
				if (timeUntilExpiry < 5 * 60 * 1000) {
					console.log('🔄 Токен скоро истечёт, обновляем...')
					try {
						const newToken = await refreshAccessToken()
						config.headers.Authorization = `Bearer ${newToken}`
					} catch (refreshError) {
						console.error('❌ Не удалось обновить токен:', refreshError)
						config.headers.Authorization = `Bearer ${token}`
					}
				} else {
					config.headers.Authorization = `Bearer ${token}`
				}
			} catch (decodeError) {
				// Если не удалось декодировать - используем как есть
				config.headers.Authorization = `Bearer ${token}`
			}
		}

		// FormData - убираем Content-Type
		if (config.data instanceof FormData) {
			delete config.headers['Content-Type']
			// Для загрузок файлов применяем увеличенный таймаут, если явно не задан иной.
			if (!config.timeout || config.timeout === CONFIG.TIMEOUT.DEFAULT) {
				config.timeout = CONFIG.TIMEOUT.UPLOAD
			}
		}

		return config
	},
	error => Promise.reject(error)
)

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================

apiClient.interceptors.response.use(
	response => response.data,
	async error => {
		if (error?.isQueuedOffline && error?.config) {
			if (!error.wasInQueue) {
				addToOfflineQueue(error.config)
			}
			const queuedError = new Error(
				'Запрос поставлен в очередь и будет выполнен после восстановления соединения'
			)
			queuedError.code = 'OFFLINE_QUEUED'
			queuedError.status = null
			queuedError.isQueuedOffline = true
			queuedError.originalError = error
			return Promise.reject(queuedError)
		}

		const originalRequest = error.config
		const status = error.response?.status

		if (status === 429 && originalRequest) {
			const method = (originalRequest.method || 'get').toLowerCase()
			const isIdempotent = ['get', 'head', 'options'].includes(method)
			const retryAfterHeader = error.response?.headers?.['retry-after']
			// Retry only when server explicitly tells us when to retry.
			// This prevents retry storms against strict API rate-limits.
			if (isIdempotent && retryAfterHeader) {
				originalRequest._retryCount = originalRequest._retryCount || 0
				if (originalRequest._retryCount < 1) {
					originalRequest._retryCount += 1
					let delayMs = 800
					const retryAfterSeconds = Number(retryAfterHeader)
					if (Number.isFinite(retryAfterSeconds)) {
						delayMs = Math.max(delayMs, retryAfterSeconds * 1000)
					} else {
						const retryAfterDate = Date.parse(retryAfterHeader)
						if (!Number.isNaN(retryAfterDate)) {
							delayMs = Math.max(delayMs, retryAfterDate - Date.now())
						}
					}
					const jitter = Math.floor(Math.random() * 200)
					await new Promise(resolve => setTimeout(resolve, Math.max(0, delayMs + jitter)))
					return apiClient(originalRequest)
				}
			}
		}

		// 401 - попытка обновить токен
		if (
			error.response?.status === 401 &&
			!originalRequest._retry &&
			!originalRequest.url?.includes('/auth/')
		) {
			originalRequest._retry = true

			if (isRefreshing) {
				// Ждём обновления токена
				return new Promise((resolve, reject) => {
					subscribeTokenRefresh((token, err) => {
						if (err) {
							reject(err)
						} else {
							originalRequest.headers.Authorization = `Bearer ${token}`
							resolve(apiClient(originalRequest))
						}
					})
				})
			}

			isRefreshing = true

			try {
				const newToken = await refreshAccessToken()
				onTokenRefreshed(newToken)
				originalRequest.headers.Authorization = `Bearer ${newToken}`
				return apiClient(originalRequest)
			} catch (refreshError) {
				onTokenRefreshFailed(refreshError)
				
				// Очищаем все данные авторизации
				clearStoredTokens()
				localStorage.removeItem('user')
				localStorage.removeItem('login_time')
				localStorage.removeItem('bitrix24_mode')
				localStorage.removeItem('bitrix24_needs_password')
				localStorage.removeItem('bitrix24_onboarding_completed')

				// Во время bootstrap входа из Bitrix24 игнорируем token_expired
				// от старой локальной сессии, чтобы не срывать онбординг.
				const bitrixBootstrapPending =
					localStorage.getItem('bitrix24_bootstrap_pending') === 'true'
				if (!bitrixBootstrapPending) {
					console.log('🔒 Токен истёк, выполняем выход...')
					window.dispatchEvent(
						new CustomEvent('auth:logout', { detail: { reason: 'token_expired' } })
					)
				}

				return Promise.reject(refreshError)
			} finally {
				isRefreshing = false
			}
		}

		// 403 - недостаточно прав
		if (error.response?.status === 403) {
			const errorCode = error.response?.data?.error?.code
			const permissionMessage =
				error.response?.data?.error?.message ||
				'У вас недостаточно прав для выполнения этого действия'
			if (errorCode === 'ACCOUNT_BLOCKED') {
				window.dispatchEvent(
					new CustomEvent('auth:blocked', {
						detail: { message: permissionMessage },
					})
				)
				return Promise.reject(error)
			}
		}

		// 413 - слишком большой запрос (часто: загрузка файла больше лимита сервера/прокси)
		if (error.response?.status === 413) {
			const enhancedError = new Error(
				'Слишком большой файл/запрос. Уменьшите размер загружаемых файлов или увеличьте лимит на сервере.'
			)
			enhancedError.status = 413
			enhancedError.code = 'PAYLOAD_TOO_LARGE'
			enhancedError.response = error.response
			enhancedError.originalError = error
			return Promise.reject(enhancedError)
		}

		// Формирование ошибки
		const errorMessage =
			error.response?.data?.error?.message ||
			error.message ||
			'Произошла ошибка'

		// Создаём расширенную ошибку
		const enhancedError = new Error(errorMessage)
		enhancedError.status = error.response?.status
		enhancedError.code = error.response?.data?.error?.code
		enhancedError.response = error.response
		enhancedError.originalError = error

		return Promise.reject(enhancedError)
	}
)

// ============================================================================
// ПУБЛИЧНЫЙ API
// ============================================================================

export default apiClient

export const api = apiClient

// Хелпер для загрузки файлов
export function uploadFile(url, file, options = {}) {
	const formData = new FormData()
	formData.append('file', file)

	return apiClient.post(url, formData, {
		timeout: CONFIG.TIMEOUT.UPLOAD,
		onUploadProgress: options.onProgress,
		...options,
	})
}

// Хелпер для запросов с отменой
export function createCancellableRequest(requestFn) {
	const controller = new AbortController()

	const promise = requestFn({ signal: controller.signal })
	promise.cancel = () => controller.abort()

	return promise
}
